"use client"

import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { useAuth } from './auth-context'
import { useToast } from '@/hooks/use-toast'
import { event } from '@/lib/analytics'

interface WishlistItem {
  id: string
  productId: string
  createdAt: string
  product: {
    id: string
    slug: string
    name: string
    price: number
    images: string | null
    category: {
      id: string
      name: string
    } | null
  }
}

interface WishlistContextType {
  wishlist: WishlistItem[]
  loading: boolean
  addToWishlist: (productId: string) => Promise<void>
  removeFromWishlist: (productId: string) => Promise<void>
  isInWishlist: (productId: string) => boolean
  refreshWishlist: () => Promise<void>
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined)

export function WishlistProvider({ children }: { children: ReactNode }) {
  const [wishlist, setWishlist] = useState<WishlistItem[]>([])
  const [loading, setLoading] = useState(true)
  const { isAuthenticated } = useAuth()
  const { toast } = useToast()

  const fetchWishlist = async () => {
    if (!isAuthenticated) {
      setWishlist([])
      setLoading(false)
      return
    }

    setLoading(true)
    try {
      const response = await fetch('/api/wishlist')
      if (response.ok) {
        const data = await response.json()
        setWishlist(data)
      }
    } catch (error) {
      console.error('Error fetching wishlist:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchWishlist()
  }, [isAuthenticated])

  const addToWishlist = async (productId: string) => {
    if (!isAuthenticated) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to add items to your wishlist.",
        variant: "default",
      })
      return
    }

    try {
      const response = await fetch('/api/wishlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId }),
      })

      if (response.ok) {
        const newItem = await response.json()
        setWishlist(prev => [newItem, ...prev])
        toast({
          title: "Added to Wishlist",
          description: "Item has been added to your wishlist.",
        })
        
        // Track wishlist add event
        event({
          action: 'add_to_wishlist',
          category: 'engagement',
          label: newItem.product?.name || `Product ${productId}`
        })
        
        console.log('[ANALYTICS] Wishlist add tracked:', {
          productId,
          productName: newItem.product?.name
        })
      } else {
        const error = await response.json()
        toast({
          title: "Error",
          description: error.error || "Failed to add to wishlist",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error('Error adding to wishlist:', error)
      toast({
        title: "Error",
        description: "Failed to add to wishlist. Please try again.",
        variant: "destructive",
      })
    }
  }

  const removeFromWishlist = async (productId: string) => {
    try {
      const response = await fetch('/api/wishlist', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId }),
      })

      if (response.ok) {
        setWishlist(prev => prev.filter(item => item.productId !== productId))
        toast({
          title: "Removed from Wishlist",
          description: "Item has been removed from your wishlist.",
        })
        
        // Track wishlist remove event
        event({
          action: 'remove_from_wishlist',
          category: 'engagement',
          label: `Product ${productId}`
        })
        
        console.log('[ANALYTICS] Wishlist remove tracked:', {
          productId
        })
      } else {
        const error = await response.json()
        toast({
          title: "Error",
          description: error.error || "Failed to remove from wishlist",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error('Error removing from wishlist:', error)
      toast({
        title: "Error",
        description: "Failed to remove from wishlist. Please try again.",
        variant: "destructive",
      })
    }
  }

  const isInWishlist = (productId: string) => {
    return wishlist.some(item => item.productId === productId)
  }

  const value = {
    wishlist,
    loading,
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
    refreshWishlist: fetchWishlist,
  }

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>
}

export function useWishlist() {
  const context = useContext(WishlistContext)
  if (context === undefined) {
    throw new Error('useWishlist must be used within a WishlistProvider')
  }
  return context
} 